import { SidebarManagerComponent } from '../..';
import { Sidebar, SidebarSide } from '../../pocos/sidebar.class';
import { DragHandler } from './handlers/drag-handler.class';
import { MenuHandler } from './handlers/menu-handler.class';
import { ResizeHandler } from './handlers/resize-handler.class';

export class SidebarComponent {
    public controlBar: HTMLDivElement;
    private _isPanelOpen: boolean = false;
    private isVisible: boolean = false;
    public static SIDEBAR_BUTTON_CLASS: string = 'wecdev-bar-button';
    public static SIDEBAR_ID: string = 'wecdev-bar';
    public static SIDEBAR_CLASS: string = 'wecdev-bar';
    public static SIDEBAR_SHADOW_CLASS: string = 'wecdev-shadow-wrapper';

    public dragHandler: DragHandler;
    public resizeHandler: ResizeHandler;
    public menuHandler: MenuHandler;

    public shadow: ShadowRoot;
    private shadowHost?: HTMLDivElement;

    // panel open state
    get isPanelOpen(): boolean {
        return this._isPanelOpen;
    }
    set isPanelOpen(value: boolean) {
        this._isPanelOpen = value;
        this.resizeHandler?.setEnabled(value);
    }

    // width
    get panelWidth(): number {
        if (this.currentSide === SidebarSide.Left || this.currentSide === SidebarSide.Right) {
            return this.sidebarSettings.size;
        } else {
            return window.innerWidth;
        }
    }
    set panelWidth(value: number) {
        if (this.currentSide === SidebarSide.Left || this.currentSide === SidebarSide.Right) {
            this.sidebarSettings.size = value;
            this.manager.resize(this.currentSide, value);
        }
    }

    // height
    get panelHeight(): number {
        if (this.currentSide === SidebarSide.Top || this.currentSide === SidebarSide.Bottom) {
            return this.sidebarSettings.size;
        } else {
            return window.innerHeight;
        }
    }
    set panelHeight(value: number) {
        if (this.currentSide === SidebarSide.Top || this.currentSide === SidebarSide.Bottom) {
            this.sidebarSettings.size = value;
            this.manager.resize(this.currentSide, value);
        }
    }

    // current side
    get currentSide(): SidebarSide {
        return this.sidebarSettings.orientation;
    }
    set currentSide(newSide: SidebarSide) {
        let oldSide: SidebarSide = this.currentSide;

        this.sidebarSettings.orientation = newSide;

        this.controlBar.classList.toggle('vertical', this.isVertical);
        this.controlBar.classList.toggle('horizontal', !this.isVertical);

        this.manager.dockTo(newSide, oldSide);
        this.manager.mouseEventHandler.resetSidebar(this);
    }

    private get isVertical(): boolean {
        return this.sidebarSettings.orientation === SidebarSide.Left || this.sidebarSettings.orientation === SidebarSide.Right;
    }

    constructor(
        private manager: SidebarManagerComponent,
        private sidebarSettings: Sidebar,
        private index: number
    ) {
        this.dragHandler = new DragHandler(this, this.index);
        this.resizeHandler = new ResizeHandler(this, this.index);
    }

    public setModulePosition(moduleName: string, x: number, y: number): void {
        this.manager.setModulePosition(this.currentSide, moduleName, x, y);
    }

    public hideBar(): void {
        if (this.sidebarExists() && this.isVisible) {
            this.controlBar.remove();
        }
        this.isVisible = false;
    }

    public showBar(): void {
        if (!this.sidebarExists() || !this.isVisible) {
            this.drawSidebar();
        }
        this.isVisible = true;
    }

    public getSidebarNode(): HTMLDivElement {
        return this.controlBar;
    }

    public updateResizable(): void {
        this.resizeHandler.updateResizable();
    }

    private sidebarExists(): boolean {
        return this.controlBar && this.controlBar !== null;
    }

    private drawSidebar(): HTMLDivElement {
        let shadowHosts = Array.from(document.getElementsByClassName(SidebarComponent.SIDEBAR_SHADOW_CLASS)) as HTMLElement[];
        let existSidebarDiv: HTMLElement | null = null;
        for (let host of shadowHosts) {
            if (!host.shadowRoot) {
                continue;
            }

            let shadow = host.shadowRoot;
            let sidebar = shadow?.getElementById(`${SidebarComponent.SIDEBAR_ID}-${this.index}`);
            if (sidebar) {
                this.shadow = shadow;
                this.injectExtensionCss();

                existSidebarDiv = sidebar;
                break;
            }
        }

        if (existSidebarDiv) {
            this.controlBar = this.shadow.getElementById(`${SidebarComponent.SIDEBAR_ID}-${this.index}`)! as HTMLDivElement;

            if ((this.controlBar as HTMLElement).dataset['sidebarOwner'] === 'true') {
                this.attachSharedListeners();
            }

            return this.controlBar;
        }

        if (this.sidebarExists()) {
            if (this.isVisible) {
                return this.controlBar;
            } else {
                this.shadow.appendChild(this.controlBar);
                return this.controlBar;
            }
        }

        let sidebarDiv: HTMLDivElement = document.createElement('div');
        let orientationClass: string = this.isVertical ? ' vertical' : ' horizontal';
        sidebarDiv.className = SidebarComponent.SIDEBAR_CLASS + orientationClass;
        sidebarDiv.id = `${SidebarComponent.SIDEBAR_ID}-${this.index}`;

        let bg: HTMLDivElement = document.createElement('div');
        bg.innerHTML = `
            <div class="wecdev-sidebar-first"></div>
            <div class="wecdev-sidebar-pattern"></div>
            <div class="wecdev-sidebar-second"></div>
        `;

        sidebarDiv.appendChild(bg);

        let closeBtn: HTMLButtonElement = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'wecdev-close-btn fa-solid fa-xmark';
        closeBtn.setAttribute('aria-label', 'Close sidebar');
        closeBtn.addEventListener('click', (e: MouseEvent) => {
            e.stopPropagation();
            this.manager.mouseEventHandler.closeSidebar(this);
        });
        sidebarDiv.appendChild(closeBtn);

        this.initShadowRoot(sidebarDiv);

        this.controlBar = sidebarDiv;
        this.isVisible = true;

        this.initSharedSidebarLogic();

        return this.controlBar;
    }

    public async initShadowRoot(sidebarDiv: HTMLDivElement): Promise<void> {
        this.createShadowHost();
        this.shadow!.appendChild(sidebarDiv);

        await this.injectCssFiles(this.manager.baseCssFiles);
        await this.injectExtensionCss();
    }

    private createShadowHost(): void {
        if (!this.shadowHost) {
            let wrapper = document.createElement('div');
            wrapper.className = SidebarComponent.SIDEBAR_SHADOW_CLASS;
            document.body.appendChild(wrapper);

            this.shadow = wrapper.attachShadow({ mode: 'open' });
            this.shadowHost = wrapper;
        }
    }

    private async injectCssFiles(files: string[]): Promise<void> {
        if (!this.shadow) {
            return;
        }

        for (let file of files) {
            let url = this.manager.assetResolver.resolve(file);
            let response = await fetch(url);
            let cssText = await response.text();
            let style = document.createElement('style');
            style.textContent = cssText;

            this.shadow.appendChild(style);
        }
    }

    private async injectExtensionCss(): Promise<void> {
        if (!this.shadow) {
            return;
        }

        let file = `assets/sidebar-${this.manager.integrationId}.css`;
        let url = this.manager.assetResolver.resolve(file);
        let response = await fetch(url);
        let cssText = await response.text();
        let style = document.createElement('style');
        style.setAttribute('data-file', url);
        style.textContent = cssText;

        this.shadow.appendChild(style);
    }

    private initSharedSidebarLogic() {
        this.resizeHandler.initResizable();
        this.dragHandler.initDocking();

        this.controlBar.dataset['sidebarOwner'] = 'true';

        this.controlBar.addEventListener('resize:start', (event: any) => {
            document.dispatchEvent(new CustomEvent('sidebar:resizeStart', { detail: { index: this.index } }));
        });

        this.controlBar.addEventListener('resize:changed', (event: any) => {
            let size = event.detail;
            document.dispatchEvent(new CustomEvent('sidebar:resizeChanged', { detail: { index: this.index, size } }));
        });

        this.controlBar.addEventListener('dock:changed', (event: any) => {
            let side = event.detail;
            document.dispatchEvent(new CustomEvent('sidebar:dockChanged', { detail: { index: this.index, side } }));
        });
    }

    private attachSharedListeners() {
        document.addEventListener('sidebar:resizeStart', (event: any) => {
            let index = event.detail.index;
            if (index === this.index) {
                this.resizeHandler.isResizing = true;
            }
        });
        document.addEventListener('sidebar:resizeChanged', (event: any) => {
            let { index, size } = event.detail;
            if (index === this.index) {
                this.panelWidth = size;
                this.panelHeight = size;

                this.resizeHandler.isResizing = false;
            }
        });

        document.addEventListener('sidebar:dockChanged', (event: any) => {
            let { index, side } = event.detail;
            if (index === this.index) {
                this.currentSide = side;
            }
        });
    }
}
